/**
 * Custom fetch mutator for Orval-generated code.
 * Handles:
 * - Base URL configuration
 * - Authentication token injection
 * - Response parsing (JSON, Blob, Text)
 *
 * Entity ID is passed via headers in individual requests:
 * sdk.invoices.createInvoice(data, { headers: { 'x-entity-id': 'entity-123' } })
 */

import { getClientHeaders } from "../client-headers";

export interface FetchConfig {
  baseUrl: string;
  getAccessToken: () => string | Promise<string>;
  clientName?: string;
  onUnauthorized?: (response: Response) => void;
}

// Global config - set by SDK initialization
let globalConfig: FetchConfig | null = null;

// Global account ID for multi-account support
let globalAccountId: string | null = null;
const ACTIVE_ACCOUNT_COOKIE = "l.account";

export function setFetchConfig(config: FetchConfig): void {
  globalConfig = config;
}

/**
 * Set the global account ID for multi-account support.
 * When set, x-account-id header is automatically added to all SDK requests.
 */
export function setAccountId(id: string | null): void {
  globalAccountId = id;
}

export function getAccountId(): string | null {
  return globalAccountId;
}

export function getFetchConfig(): FetchConfig {
  if (!globalConfig) {
    throw new Error("SDK not initialized. Call new SpaceInvoices() first.");
  }
  return globalConfig;
}

function getBrowserCookie(name: string): string | null {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  if (!match) return null;

  return decodeURIComponent(match[1]);
}

/**
 * SDK Error class for API errors
 */
export class SDKError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: unknown,
  ) {
    super(`${status} ${statusText}`);
    this.name = "SDKError";
  }
}

/**
 * Convert HeadersInit to a plain object for merging.
 */
function normalizeHeaders(headers?: HeadersInit): Record<string, string> {
  if (!headers) return {};

  if (headers instanceof Headers) {
    const obj: Record<string, string> = {};
    headers.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }

  if (Array.isArray(headers)) {
    const obj: Record<string, string> = {};
    for (const [key, value] of headers) {
      obj[key] = value;
    }
    return obj;
  }

  return headers as Record<string, string>;
}

/**
 * Custom fetch implementation used by all generated API functions.
 * This is the mutator function that Orval will use for all API calls.
 *
 * Returns an object with { data, status, headers } to match the expected response type.
 */
export async function customFetch<T>(url: string, options: RequestInit): Promise<T> {
  const config = getFetchConfig();

  // Resolve access token (may be async)
  const token = await Promise.resolve(config.getAccessToken());

  // Build full URL
  const fullUrl = `${config.baseUrl}${url}`;

  // Merge headers - normalize incoming headers first
  const incomingHeaders = normalizeHeaders(options.headers);
  const headers: Record<string, string> = {
    ...incomingHeaders,
  };

  if (config.clientName) {
    Object.assign(headers, getClientHeaders(config.clientName));
  }

  // Add authorization
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Add account ID for multi-account support. Fall back to the browser cookie
  // when the in-memory selection has not been restored yet.
  const resolvedAccountId =
    headers["x-account-id"] || headers["X-Account-Id"] || globalAccountId || getBrowserCookie(ACTIVE_ACCOUNT_COOKIE);

  if (resolvedAccountId && !headers["x-account-id"] && !headers["X-Account-Id"]) {
    headers["x-account-id"] = resolvedAccountId;
  }

  // Execute request
  const response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  // Handle unauthorized
  if (response.status === 401 && config.onUnauthorized) {
    config.onUnauthorized(response);
  }

  // Parse response body based on content type
  let data: unknown;

  const contentLength = response.headers.get("content-length");
  const contentType = response.headers.get("content-type");

  if (contentLength === "0" || response.status === 204) {
    // Empty response
    data = undefined;
  } else if (
    contentType?.includes("application/pdf") ||
    contentType?.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document") ||
    contentType?.includes("application/octet-stream") ||
    contentType?.includes("application/zip") ||
    contentType?.includes("application/x-zip-compressed") ||
    contentType?.includes("image/")
  ) {
    // Binary responses (PDF, ZIP, images, generic binary from CDN redirects)
    data = await response.blob();
  } else if (contentType?.includes("application/xml") || contentType?.includes("text/xml")) {
    // XML responses (e.g. e-SLOG downloads)
    data = await response.text();
  } else if (contentType?.includes("text/html")) {
    // HTML responses (previews)
    data = await response.text();
  } else {
    // Default: JSON (handle errors gracefully)
    try {
      data = await response.json();
    } catch {
      data = undefined;
    }
  }

  // Return in the format expected by generated code
  return {
    data,
    status: response.status,
    headers: response.headers,
  } as T;
}

export default customFetch;
